import React, { useContext, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import styles from "./active-loans.module.scss";
import alert_green from "../../../assets/alert_green.svg";
import plus_green from "../../../assets/plus_icon_green.svg";
import close_icon from "../../../assets/close.svg";
import InputWithHeader from "../../../components/Input/InputWithHeader";
import ModalLayout from "../../../components/ModalLayout";
import PopUp from "../../../components/Insurance/PopUp";
import UseActiveLoans from "./api-calls";
import {
  decodeBase64,
  formatAmountNaira,
  getQuery,
} from "../../../utils/generalUtils";
import { ExistingLoan } from "../../../types/mortgage";
import ToastContext from "../../../contexts/ToastContext";

const locationOptions = [
  { name: "Nigeria", value: 0 },
  { name: "Diaspora", value: 1 },
];
 
type FormValues = {
  additionalLoans: ExistingLoan[];
};

const emptyLoan: ExistingLoan = {
  outstandingLoanAmount: 0,
  monthlyRepayment: 0,
  loanTenor: 0,
  remainingTenure: 0,
  lendingInstitution: "",
};

const loanEntrySchema = yup.object({
  outstandingLoanAmount: yup
    .string()
    .defined()
    .required("Outstanding amount is required"),
  monthlyRepayment: yup
    .string()
    .defined()
    .required("Monthly repayment is required"),
  loanTenor: yup.string().defined().required("Loan tenor is required"),
  remainingTenure: yup
    .string()
    .defined()
    .required("Remaining tenor is required"),
  lendingInstitution: yup
    .string()
    .defined()
    .required("Lending institution is required"),
});

const buildSchema = () =>
  yup.object({
    additionalLoans: yup.array().of(loanEntrySchema).defined(),
  });

export const toNumber = (val: number | string): number =>
  Number(String(val).replace(/[^0-9.]/g, "")) || 0;

const ActiveLoans = () => {
  const encodedBvn = getQuery("app");
  const encodedMortgageAppId = getQuery("dippa") || "";
  const mortgageApplicationId = toNumber(decodeBase64(encodedMortgageAppId));

  const [location, setLocation] = useState<number | undefined>(undefined);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const isLocationSelected = location === 0 || location === 1;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(buildSchema()) as any,
    defaultValues: { additionalLoans: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "additionalLoans",
  });

  const { openError } = useContext(ToastContext);

  const {
    models: { creditCheckResponses, creditCheckBlocked, creditCheckErrorMessage },
    operations: { submitLoans },
  } = UseActiveLoans({
    mortgageApplicationId,
    location,
    encodedBvn,
    encodedMortgageAppId,
  });

  // Only the records the bureau actually flagged as active loans.
  const activeLoans =
    creditCheckResponses?.filter((r) => r.hasActiveLoans) ?? [];

  const onSubmit = (data: FormValues) => {
    if (creditCheckBlocked) {
      openError(
        creditCheckErrorMessage ||
          "Unable to verify your credit history at this time."
      );
      return;
    }

    const additionalLoans: ExistingLoan[] = (data?.additionalLoans ?? []).map(
      (loan) => ({
        outstandingLoanAmount: toNumber(loan.outstandingLoanAmount),
        monthlyRepayment: toNumber(loan.monthlyRepayment),
        loanTenor: toNumber(loan.loanTenor),
        remainingTenure: toNumber(loan.remainingTenure),
        lendingInstitution: loan.lendingInstitution,
      })
    );

    const creditCheckLoans: ExistingLoan[] = activeLoans.map((r) => ({
      outstandingLoanAmount: r.totalOutstandingDebt,
      monthlyRepayment: r.existingMonthlyRepayment,
      loanTenor: 0,
      remainingTenure: 0,
      lendingInstitution: "",
    }));

    submitLoans({
      hasAdditionalLoans: additionalLoans.length > 0,
      mortgageApplicationId,
      existingLoans: [...creditCheckLoans, ...additionalLoans],
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.container}>
        {isLocationSelected && activeLoans?.length > 0 ? (
          <>
            <span className={styles.bold_text}>You have active Loans</span>

            <span className={styles.light_text}>
              Our credit bureau check found existing loan obligations linked to
              your BVN. Please review and confirm the details below before
              proceeding.
            </span>
          </>
        ) : (
          <>
            <span className={styles.bold_text}>You have no active Loans</span>

            <span className={styles.light_text}>
              Our credit bureau check found no existing loan obligations linked
              to your BVN.
            </span>
          </>
        )}

        <div
          className={
            isLocationSelected && activeLoans?.length > 1
              ? styles.data_cont
              : styles.no_display
          }
        >
          <div className={styles.flex_row}>
            <img src={alert_green} alt="Verified" className={styles.alert} />
            <span className={styles.text_highlighted}>
              Multiple loans found
            </span>
          </div>
          <span className={styles.data_text}>
            These will be factored into your debt-to-income ratio calculation.
          </span>
        </div>
        <div className={styles.grid}>
          <InputWithHeader
            header="Where Are You Located?"
            placeholder="Select Location"
            type="dropdown"
            value={
              locationOptions.find((l) => l.value === location)?.name ?? ""
            }
            onPressDropDown={() => setLocationDropdownOpen(true)}
          />
        </div>

        {creditCheckBlocked && (
          <div className={styles.credit_check_error}>
            <span>{creditCheckErrorMessage}</span>
          </div>
        )}

        {isLocationSelected ? (
          <>
            {activeLoans.map((loan, i) => (
              <div className={styles.grid} key={i}>
                <InputWithHeader
                  header="Outstanding Amount"
                  placeholder=""
                  type="amount"
                  value={
                    loan.totalOutstandingDebt
                      ? formatAmountNaira(loan.totalOutstandingDebt)
                      : ""
                  }
                  editable={false}
                />
                <InputWithHeader
                  header="Monthly Repayment"
                  placeholder=""
                  type="string"
                  value={
                    loan.existingMonthlyRepayment
                      ? formatAmountNaira(loan.existingMonthlyRepayment)
                      : ""
                  }
                  editable={false}
                />
                <InputWithHeader
                  header="Loan Tenor"
                  placeholder=""
                  type="string"
                  value=""
                  editable={false}
                />
                <InputWithHeader
                  header="Remaining Tenor"
                  placeholder=""
                  type="string"
                  value=""
                  editable={false}
                />
                <div className={styles["span-2"]}>
                  <InputWithHeader
                    header="Lending Institution"
                    placeholder=""
                    type="string"
                    value=""
                    editable={false}
                  />
                </div>
              </div>
            ))}

            <div
              className={styles.add_more_button}
              role="button"
              onClick={() => append({ ...emptyLoan })}
            >
              <img src={plus_green} alt="Add" className={styles.alert} />
              <span className={styles.add_more_txt}>Add New Loan</span>
            </div>

            {fields.map((field, index) => (
              <div key={field.id} className={styles.additional_loan_block}>
                <div className={styles.remove_row}>
                  <div
                    className={styles.remove_button}
                    role="button"
                    onClick={() => remove(index)}
                  >
                    <img
                      src={close_icon}
                      alt="Remove"
                      className={styles.close_icon}
                    />
                    <span className={styles.remove_txt}>Remove</span>
                  </div>
                </div>

                <div className={styles.grid}>
                  <Controller
                    name={`additionalLoans.${index}.outstandingLoanAmount`}
                    control={control}
                    render={({ field: f }) => (
                      <InputWithHeader
                        header="Outstanding Amount"
                        placeholder="Enter outstanding amount"
                        type="amount"
                        value={f.value}
                        formKey={`additionalLoans.${index}.outstandingLoanAmount`}
                        handleChange={(_, val) => f.onChange(val)}
                        error={
                          errors.additionalLoans?.[index]?.outstandingLoanAmount
                            ?.message
                        }
                      />
                    )}
                  />
                  <Controller
                    name={`additionalLoans.${index}.monthlyRepayment`}
                    control={control}
                    render={({ field: f }) => (
                      <InputWithHeader
                        header="Monthly Repayment"
                        placeholder="Enter monthly repayment"
                        type="string"
                        value={f.value}
                        formKey={`additionalLoans.${index}.monthlyRepayment`}
                        handleChange={(_, val) => f.onChange(val)}
                        error={
                          errors.additionalLoans?.[index]?.monthlyRepayment
                            ?.message
                        }
                      />
                    )}
                  />
                  <Controller
                    name={`additionalLoans.${index}.loanTenor`}
                    control={control}
                    render={({ field: f }) => (
                      <InputWithHeader
                        header="Loan Tenor"
                        placeholder="e.g. 3 months"
                        type="string"
                        value={f.value}
                        formKey={`additionalLoans.${index}.loanTenor`}
                        handleChange={(_, val) => f.onChange(val)}
                        error={
                          errors.additionalLoans?.[index]?.loanTenor?.message
                        }
                      />
                    )}
                  />
                  <Controller
                    name={`additionalLoans.${index}.remainingTenure`}
                    control={control}
                    render={({ field: f }) => (
                      <InputWithHeader
                        header="Remaining Tenor"
                        placeholder="Enter remaining tenor"
                        type="string"
                        value={f.value}
                        formKey={`additionalLoans.${index}.remainingTenure`}
                        handleChange={(_, val) => f.onChange(val)}
                        error={
                          errors.additionalLoans?.[index]?.remainingTenure
                            ?.message
                        }
                      />
                    )}
                  />
                  <div className={styles["span-2"]}>
                    <Controller
                      name={`additionalLoans.${index}.lendingInstitution`}
                      control={control}
                      render={({ field: f }) => (
                        <InputWithHeader
                          header="Lending Institution"
                          placeholder="Enter lending institution"
                          type="string"
                          value={f.value}
                          formKey={`additionalLoans.${index}.lendingInstitution`}
                          handleChange={(_, val) => f.onChange(val)}
                          error={
                            errors.additionalLoans?.[index]?.lendingInstitution
                              ?.message
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="submit"
              className={styles.button}
              disabled={creditCheckBlocked}
              style={{
                opacity: creditCheckBlocked ? 0.5 : 1,
                cursor: creditCheckBlocked ? "not-allowed" : "pointer",
              }}
            >
              Continue
            </button>
          </>
        ) : null}
      </div>

      <ModalLayout
        modalOpen={locationDropdownOpen}
        onClickOut={() => setLocationDropdownOpen(false)}
      >
        <PopUp
          options={locationOptions.map((l) => l.name)}
          click={(name) => {
            const val = locationOptions.find((l) => l.name === name)?.value;
            setLocation(val);
            setLocationDropdownOpen(false);
          }}
        />
      </ModalLayout>
    </form>
  );
};

export default ActiveLoans;
